export function normalizeReturnTo(target, fallback = '/') {
  const safeFallback = typeof fallback === 'string' && fallback.startsWith('/') ? fallback : '/';
  if (!target && target !== '') {
    return safeFallback;
  }

  const raw = String(target ?? '').trim();
  if (!raw) {
    return safeFallback;
  }

  try {
    if (/^https?:\/\//i.test(raw)) {
      if (typeof window === 'undefined') {
        return safeFallback;
      }
      const url = new URL(raw);
      if (url.origin !== window.location.origin) {
        return safeFallback;
      }
      const path = `${url.pathname}${url.search}${url.hash}`;
      return path || safeFallback;
    }

    if (raw.startsWith('/')) {
      return raw;
    }

    if (raw.startsWith('#')) {
      return `${safeFallback}${raw}`;
    }

    return `/${raw.replace(/^\/+/, '')}`;
  } catch (error) {
    console.warn('[returnTo] failed to normalize target', error);
    return safeFallback;
  }
}

export function buildReturnToFromLocation(locationLike, fallback = '/') {
  const path = locationLike?.pathname ?? '';
  const search = locationLike?.search ?? '';
  const hash = locationLike?.hash ?? '';
  const candidate = `${path || ''}${search || ''}${hash || ''}`;
  return normalizeReturnTo(candidate || fallback, fallback);
}

export function isPrivanaDeepLinkOrigin(origin) {
  if (!origin || typeof origin !== 'string') return false;
  try {
    const url = new URL(origin);
    if (url.protocol !== 'https:' && url.hostname !== 'localhost' && url.hostname !== '127.0.0.1') {
      return false;
    }
    return [
      'app.privana.club',
      'starter.privana.club',
      'bagcart.privana.club',
      'localhost',
      '127.0.0.1',
    ].includes(url.hostname);
  } catch {
    return false;
  }
}

export function buildAbsoluteReturnTo(origin, returnTo = '/') {
  const normalizedReturnTo = normalizeReturnTo(returnTo, '/');
  if (!isPrivanaDeepLinkOrigin(origin)) {
    return normalizedReturnTo;
  }
  return `${String(origin).replace(/\/$/, '')}${normalizedReturnTo}`;
}

export function getAuth0RedirectUri() {
  if (typeof window === 'undefined') return '';
  const hostname = window.location?.hostname || '';
  if (hostname === 'dininghost.privana.club' || hostname.startsWith('dininghost.')) {
    return `${window.location.origin}/auth/callback`;
  }
  return window.location.origin;
}

export function withAuth0RedirectUri(authorizationParams = {}) {
  return {
    ...(authorizationParams || {}),
    redirect_uri: getAuth0RedirectUri(),
  };
}

export function parseClubCodeFromPathname(pathname) {
  if (!pathname) return null;
  const normalizedPath = String(pathname).split('?')[0].split('#')[0];
  const match = normalizedPath.match(/^\/(\d{6})(?:\/|$)/);
  return match ? match[1] : null;
}

export const LAST_LOGOUT_RETURN_TO_KEY = 'privana:last_logout_return_to';
export const LAST_LOGOUT_CLUB_CODE_KEY = 'privana:last_logout_club_code';

function setStorageItem(storage, key, value) {
  if (!storage) return;
  try {
    if (value) {
      storage.setItem(key, value);
    } else {
      storage.removeItem(key);
    }
  } catch (error) {
    console.warn('[returnTo] failed to persist logout context', error);
  }
}

function getStorageItem(storage, key) {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch (error) {
    console.warn('[returnTo] failed to read logout context', error);
    return null;
  }
}

export function buildLoggedOutPath() {
  return '/login?logged_out=1';
}

export function buildClubRootFromLocation(locationLike) {
  const clubCode = parseClubCodeFromPathname(locationLike?.pathname ?? '');
  return clubCode ? `/${clubCode}` : null;
}

export function rememberLogoutContext(locationLike) {
  if (typeof window === 'undefined') return null;

  const clubCode = parseClubCodeFromPathname(locationLike?.pathname ?? '');
  const returnTo = clubCode ? `/${clubCode}` : null;

  setStorageItem(window.sessionStorage, LAST_LOGOUT_RETURN_TO_KEY, returnTo);
  setStorageItem(window.localStorage, LAST_LOGOUT_RETURN_TO_KEY, returnTo);
  setStorageItem(window.sessionStorage, LAST_LOGOUT_CLUB_CODE_KEY, clubCode);
  setStorageItem(window.localStorage, LAST_LOGOUT_CLUB_CODE_KEY, clubCode);

  return returnTo;
}

export function getRememberedLogoutReturnTo() {
  if (typeof window === 'undefined') return null;
  const stored =
    getStorageItem(window.sessionStorage, LAST_LOGOUT_RETURN_TO_KEY) ||
    getStorageItem(window.localStorage, LAST_LOGOUT_RETURN_TO_KEY);
  return stored ? normalizeReturnTo(stored, '/') : null;
}

export function getRememberedLogoutClubCode() {
  if (typeof window === 'undefined') return null;
  const stored =
    getStorageItem(window.sessionStorage, LAST_LOGOUT_CLUB_CODE_KEY) ||
    getStorageItem(window.localStorage, LAST_LOGOUT_CLUB_CODE_KEY);
  return parseClubCodeFromPathname(`/${stored || ''}`);
}

export function buildLogoutReturnTo(locationLike, _fallback = '/') {
  rememberLogoutContext(locationLike);
  const targetPath = buildLoggedOutPath();

  if (typeof window === 'undefined') {
    return targetPath;
  }

  return `${window.location.origin}${targetPath}`;
}
